import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  startLogin,
  finishLogin,
  currentToken,
  logout as clearToken,
  decodeClaims,
  isExpired,
  type TokenClaims,
} from './pkce';

type Status = 'checking' | 'logged_out' | 'logged_in';

interface AuthState {
  status: Status;
  token: string | null;
  claims: TokenClaims | null;
  error: string | null;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** Guarda de rota da SPA inteira (ADR 0019 §1) — não tem página pública, então isso vira a raiz. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('checking');
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    if (code) {
      (async () => {
        try {
          await finishLogin(code);
          window.history.replaceState(null, '', '/');
          settle();
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Não foi possível entrar');
          setStatus('logged_out');
        }
      })();
    } else {
      settle();
    }
  }, []);

  function settle() {
    const t = currentToken();
    const claims = t ? decodeClaims(t) : null;
    if (t && !isExpired(claims)) {
      setToken(t);
      setStatus('logged_in');
    } else {
      clearToken();
      setToken(null);
      setStatus('logged_out');
    }
  }

  const claims = token ? decodeClaims(token) : null;

  const value: AuthState = {
    status,
    token,
    claims,
    error,
    login: () => {
      setError(null);
      startLogin().catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    },
    logout: () => {
      clearToken();
      setToken(null);
      setStatus('logged_out');
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fora do AuthProvider');
  return ctx;
}
